"use client"

import { observer } from "mobx-react-lite"
import type React from "react"
import { useEffect, useMemo, useState } from "react"
import Dropdown from "../../shared/components/dropdown/Dropdown"
import ErrorBoundary from "../../shared/components/error-boundary/ErrorBoundary"
import { useAppContext } from "../../shared/functions/Context"
import { dataFormat } from "../../shared/functions/Directives"
import { MAIL_EMAIL, MAIL_MIDTERM_RATINGS_DISPUTE_ME } from "../../shared/functions/mailMessages"
import showModalFromId from "../../shared/functions/ModalShow"
import useAutoSave from "../../shared/hooks/useAutoSave"
import { ALL_TAB, fullPerspectiveName } from "../../shared/interfaces/IPerspectiveTabs"
import type Measure from "../../shared/models/Measure"
import type Objective from "../../shared/models/Objective"
import type { IScorecardMetadata } from "../../shared/models/ScorecardMetadata"
import EmptyError from "../admin-settings/EmptyError"
import MODAL_NAMES from "../dialogs/ModalName"
import Toolbar from "../shared/components/toolbar/Toolbar"
import { rateColor, q4EmpRating, q4FinalRating, q4SuperRating } from "../shared/functions/Scorecard"
import NoPerformanceData from "./NoPerformanceData"
import NoMeasures from "./no-measures/NoMeasures"
import type { IUser } from "../../shared/models/User"
import { useParams } from "react-router-dom"
import { MessageSquare, AlertCircle, CheckCircle2, Target, Award } from "lucide-react"
interface IQ4ReviewMeasureTableItemProps {
  measure: Measure
  canUpdate: boolean
  q4Rated: boolean
}

const MeasureCard = observer((props: IQ4ReviewMeasureTableItemProps) => {
  const { api, store, ui } = useAppContext()
  const measure = props.measure.asJson
  const { autoSave } = useAutoSave({ timeout: 2000 })

  const dataType = measure.dataType
  const dataSymbol = measure.dataSymbol

  const [unsavedChanges, setunSavedChanges] = useState(false)
  const [ratingCss, setRatingCss] = useState("")

  const readMeasureComment = () => {
    store.measure.select(measure)
    // showModalFromId(MODAL_NAMES.EXECUTION.MEASURE_COMMENTS_Q4_MODAL)
  }

  const handleDispute = () => {
    store.measure.select(measure)
    showModalFromId(MODAL_NAMES.EXECUTION.SCORECARD_RESOLVE_DISPUTE_Q4_MODAL)
  }

  const measureReadOnly = () => {
    store.measure.select(measure)
    showModalFromId(MODAL_NAMES.EXECUTION.MEASURE_READ_ONLY_MODAL)
  }

  useEffect(() => {
    if (measure.finalRating2) {
      const rateCss = rateColor(Number(measure.finalRating2), measure.isUpdated)
      setRatingCss(rateCss)
    } else if (measure.supervisorRating2) {
      const ratingCss = rateColor(Number(measure.supervisorRating2), measure.isUpdated)
      setRatingCss(ratingCss)
    } else {
      const ratingCss = rateColor(measure.autoRating2 ?? 0, measure.isUpdated)
      setRatingCss(ratingCss)
    }
  }, [measure, ratingCss])

  const disputeExists =
    measure.finalAssessmentDisputeReason ||
    measure.finalAssessmentPreferredRating ||
    measure.finalAssessmentDisputeType ||
    measure.finalAssessmentAdditionalComments

  const isResolved = Boolean(measure.finalAssessmentIsResolved)
  // max: ensured the resolved final disputes broadcast a green badge for quick status checks
  const disputeContainerClass = isResolved
    ? "mt-4 p-4 bg-green-50 border-l-4 border-[#4bb543] rounded-r-lg"
    : "mt-4 p-4 bg-orange-50 border-l-4 border-[#faa05a] rounded-r-lg"
  const statusBadgeClass = isResolved
    ? "inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#dff5e5] text-[#2b6a2f] text-xs font-semibold"
    : "inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#ffe8cc] text-[#a35c22] text-xs font-semibold"

  const getRatingColor = (rating: number | null) => {
    if (!rating) return "bg-gray-100 text-gray-600"
    if (rating >= 4) return "bg-green-100 text-[#4bb543]"
    if (rating >= 3) return "bg-blue-100 text-[#84cbe9]"
    if (rating >= 2) return "bg-orange-100 text-[#faa05a]"
    return "bg-red-100 text-[#dc3545]"
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow mb-4">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Target className="w-4 h-4 text-[#84cbe9]" />
            {measure.description}
          </h4>
          <button
            onClick={readMeasureComment}
            className="text-sm text-[#84cbe9] hover:text-[#3f6a8e] flex items-center gap-1 transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            View Comments
          </button>
        </div>

        <div className="flex gap-2">
          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${getRatingColor(measure.autoRating2)}`}>
            E: {measure.autoRating2?.toFixed(2) || "N/A"}
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${getRatingColor(measure.supervisorRating2)}`}>
            S: {measure.supervisorRating2?.toFixed(2) || "N/A"}
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${getRatingColor(measure.finalRating2)}`}>
            F: {measure.finalRating2?.toFixed(2) || "N/A"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-4 mb-4">
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-1">Rating 1</p>
          <p className="font-semibold text-gray-900">{dataFormat(dataType, measure.rating1, dataSymbol)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-1">Rating 2</p>
          <p className="font-semibold text-gray-900">{dataFormat(dataType, measure.rating2, dataSymbol)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-1">Rating 3</p>
          <p className="font-semibold text-gray-900">{dataFormat(dataType, measure.rating3, dataSymbol)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-1">Rating 4</p>
          <p className="font-semibold text-gray-900">{dataFormat(dataType, measure.rating4, dataSymbol)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-1">Rating 5</p>
          <p className="font-semibold text-gray-900">{dataFormat(dataType, measure.rating5, dataSymbol)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-1">Target</p>
          <p className="font-semibold text-[#84cbe9]">{dataFormat(dataType, measure.annualTarget, dataSymbol)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-1">Progress</p>
          <p className="font-semibold text-[#3f6a8e]">{dataFormat(dataType, measure.quarter4Actual, dataSymbol)}</p>
        </div>
      </div>

      {disputeExists && (
        <div className={disputeContainerClass}>
          <div className="flex items-start gap-3 mb-3">
            <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isResolved ? "text-[#4bb543]" : "text-[#faa05a]"}`} />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-semibold text-gray-900">Dispute Information</h5>
                <span className={statusBadgeClass}>
                  {isResolved ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                  {isResolved ? "Resolved" : "Awaiting Resolution"}
                </span>
              </div>

              <div className="space-y-2 text-sm">
                {measure.finalAssessmentDisputeReason && (
                  <div>
                    <span className="font-medium text-gray-700">Reason:</span>
                    <p className="text-gray-600 mt-1">{measure.finalAssessmentDisputeReason}</p>
                  </div>
                )}

                {measure.finalAssessmentPreferredRating && (
                  <div>
                    <span className="font-medium text-gray-700">Preferred Rating:</span>
                    <span className="ml-2 px-2 py-1 bg-white rounded text-[#faa05a] font-semibold">
                      {measure.finalAssessmentPreferredRating}
                    </span>
                  </div>
                )}

                {measure.finalAssessmentDisputeType && (
                  <div>
                    <span className="font-medium text-gray-700">Type:</span>
                    <span className="ml-2 text-gray-600">{measure.finalAssessmentDisputeType}</span>
                  </div>
                )}

                {measure.finalAssessmentAdditionalComments && (
                  <div>
                    <span className="font-medium text-gray-700">Additional Comments:</span>
                    <p className="text-gray-600 mt-1">{measure.finalAssessmentAdditionalComments}</p>
                  </div>
                )}

                {measure.finalAssessmentEmployeeDisputeResolution && (
                  <div className="pt-2 border-t border-orange-200">
                    <span className="font-medium text-gray-700">Employee Resolution:</span>
                    <p className="text-gray-600 mt-1">{measure.finalAssessmentEmployeeDisputeResolution}</p>
                  </div>
                )}

                {measure.finalAssessmentSupervisorDisputeResolution && (
                  <div className="pt-2 border-t border-orange-200">
                    <span className="font-medium text-gray-700">Supervisor Resolution:</span>
                    <p className="text-gray-600 mt-1">{measure.finalAssessmentSupervisorDisputeResolution}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={handleDispute}
            className={`w-full mt-3 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${isResolved ? "bg-green-100 text-[#4bb543] hover:bg-green-200" : "bg-[#dc3545] text-white hover:bg-red-600"
              }`}
          >
            {isResolved ? (
              <>
                <CheckCircle2 className="w-4 h-4" />
                View Resolution
              </>
            ) : (
              <>
                <AlertCircle className="w-4 h-4" />
                Resolve Dispute
              </>
            )}
          </button>
        </div>
      )}
    </div>
  )
})

interface IQ4ReviewProps {
  measures: Measure[]
  canUpdate: boolean
  q4Rated: boolean
}
const MeasureTable = observer((props: IQ4ReviewProps) => {
  const { measures, canUpdate, q4Rated } = props

  return (
    <ErrorBoundary>
      <div className="space-y-4">
        {measures.length !== 0 && (
          <>
            {measures.map((measure) => (
              <ErrorBoundary key={measure.asJson.id}>
                <MeasureCard measure={measure} canUpdate={canUpdate} q4Rated={q4Rated} />
              </ErrorBoundary>
            ))}
          </>
        )}
        {measures.length === 0 && <NoMeasures />}
      </div>
    </ErrorBoundary>
  )
})

interface IRatingProps {
  measures: Measure[]
}
const ScorecardRatings = observer((props: IRatingProps) => {
  const { measures } = props
  const { api, store } = useAppContext()
  const { uid } = useParams()

  const $measures = measures.map((measure) => measure.asJson)

  const rating1 = q4EmpRating($measures)
  const rating2 = q4SuperRating($measures)
  const rating3 = q4FinalRating($measures)

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return "bg-green-100 text-[#4bb543] border-green-200"
    if (rating >= 3) return "bg-blue-100 text-[#84cbe9] border-blue-200"
    if (rating >= 2) return "bg-orange-100 text-[#faa05a] border-orange-200"
    return "bg-red-100 text-[#dc3545] border-red-200"
  }

  useEffect(() => {
    const loadData = async () => {
      await api.companyScorecardMetadata.getAll()
    }
    loadData()
  }, [])

  return (
    <ErrorBoundary>
      <div className="bg-gradient-to-r from-[#84cbe9] to-[#3f6a8e] rounded-xl shadow-lg p-8 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
          <Award className="w-6 h-6" />
          Overall Q4 Scorecard Ratings
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg p-6 text-center">
            <p className="text-sm text-gray-600 mb-2">Employee Rating</p>
            <div className={`inline-block px-6 py-3 rounded-lg border-2 ${getRatingColor(rating1)}`}>
              <span className="text-3xl font-bold">{rating1.toFixed(2)}</span>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 text-center">
            <p className="text-sm text-gray-600 mb-2">Supervisor Rating</p>
            <div className={`inline-block px-6 py-3 rounded-lg border-2 ${getRatingColor(rating2)}`}>
              <span className="text-3xl font-bold">{rating2.toFixed(2)}</span>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 text-center">
            <p className="text-sm text-gray-600 mb-2">Final Rating</p>
            <div className={`inline-block px-6 py-3 rounded-lg border-2 ${getRatingColor(rating3)}`}>
              <span className="text-3xl font-bold">{rating3.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  )
})

interface ObjectiveItemProps {
  objective: Objective
  children?: React.ReactNode
  handleComments: () => void
}
const ObjectiveItem = observer((props: ObjectiveItemProps) => {
  const { objective, handleComments, children } = props
  const { weight, description, perspective } = objective.asJson
  const { rate, isUpdated } = objective.q4Rating

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return "bg-green-100 text-[#4bb543] border-green-200"
    if (rating >= 3) return "bg-blue-100 text-[#84cbe9] border-blue-200"
    if (rating >= 2) return "bg-orange-100 text-[#faa05a] border-orange-200"
    return "bg-red-100 text-[#dc3545] border-red-200"
  }

  return (
    <ErrorBoundary>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className={`px-4 py-2 rounded-lg border-2 font-bold text-lg ${getRatingColor(rate)}`}>
                {rate.toFixed(1)}
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{description}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                    {fullPerspectiveName(perspective)}
                  </span>
                  <span className="text-sm font-medium text-[#84cbe9]">Weight: {weight || 0}%</span>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleComments}
            className="p-2 text-[#84cbe9] hover:bg-blue-50 rounded-lg transition-colors"
            title="View Comments"
          >
            <MessageSquare className="w-5 h-5" />
          </button>
        </div>

        <div>{children}</div>
      </div>
    </ErrorBoundary>
  )
})

interface IObjectivesProps {
  tab: string
  objectives: Objective[]
  canUpdate: boolean
  q4Rated: boolean
}

const ObjectivesList = observer((props: IObjectivesProps) => {
  const { store } = useAppContext()
  const { tab, objectives, canUpdate, q4Rated } = props

  const handleComments = (objective: Objective) => {
    store.objective.select(objective.asJson)
    showModalFromId(MODAL_NAMES.EXECUTION.OBJECTIVE_Q4_COMMENT_MODAL)
  }

  const _objectives = useMemo(() => {
    return tab === ALL_TAB.id ? objectives : objectives.filter((o) => o.asJson.perspective === tab)
  }, [store.objective, tab, objectives])

  return (
    <ErrorBoundary>
      <div className="objective-table uk-margin">
        <ErrorBoundary>
          {_objectives.map((objective) => (
            <ObjectiveItem
              handleComments={() => handleComments(objective)}
              key={objective.asJson.id}
              objective={objective}
            >
              <MeasureTable measures={objective.measures} canUpdate={canUpdate} q4Rated={q4Rated} />
            </ObjectiveItem>
          ))}
        </ErrorBoundary>
        <ErrorBoundary>{!objectives.length && <EmptyError errorMessage="No objective found" />}</ErrorBoundary>
      </div>
    </ErrorBoundary>
  )
})

interface IQ4Props {
  agreement: IScorecardMetadata
  selectedUser: IUser
  currentUser: IUser
  measures: Measure[]
  objectives: Objective[]
}

const EmployeeQ4ReviewCycleResolveDispute = observer((props: IQ4Props) => {
  const { store, ui, api } = useAppContext()
  const [tab, setTab] = useState(ALL_TAB.id)

  const { agreement, selectedUser, currentUser, measures, objectives } = props
  const q4Rated = agreement.assessmentRated

  const isDisabled = useMemo(
    () => !(agreement.quarter4Review.status === "submitted"),
    [agreement.quarter4Review.status],
  )

  const canUpdate = useMemo(() => agreement.quarter4Review.status === "submitted", [agreement])

  const incompleteReviewError = measures.some((m) => m.asJson.supervisorRating2 === null)

  const onSubmit = async () => {
    if (incompleteReviewError) {
      ui.snackbar.load({
        id: Date.now(),
        type: "danger",
        message: "Incomplete review. Some measures are not rated.",
        timeoutInMs: 10000,
      })
      return
    } else if (!selectedUser || !currentUser) {
      ui.snackbar.load({
        id: Date.now(),
        type: "danger",
        message: "User not found, email not sent.",
        timeoutInMs: 10000,
      })
      return
    }

    const { MY_SUBJECT, MY_BODY } = MAIL_MIDTERM_RATINGS_DISPUTE_ME(selectedUser.displayName, currentUser.displayName)

    const $agreement: IScorecardMetadata = { ...agreement, assessmentRated: true }
    await api.individualScorecard.update($agreement)
    await api.mail.sendMailCC([ "systems@lotsinsights.com",selectedUser.email!], [currentUser.email!], MAIL_EMAIL, MY_SUBJECT, MY_BODY)
    ui.snackbar.load({
      id: Date.now(),
      type: "success",
      message: "Email sent.",
      timeoutInMs: 10000,
    })
  }


  if (agreement.quarter4Review.status === "pending" || agreement.quarter4Review.status === "in-progress")
    return (
      <ErrorBoundary>
        <NoPerformanceData title="Final assessment progress not submitted." />
      </ErrorBoundary>
    )

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8">
        <div className="w-full px-4 sm:px-6 lg:px-8">


          <ErrorBoundary>
            <ScorecardRatings measures={measures} />
          </ErrorBoundary>

          <ErrorBoundary>
            <ObjectivesList tab={tab} objectives={objectives} canUpdate={canUpdate} q4Rated={q4Rated} />
          </ErrorBoundary>
        </div>
      </div>
    </ErrorBoundary>
  )
})

export default EmployeeQ4ReviewCycleResolveDispute
